import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {validators} from '@emma-labs/shared/angular';
import {ENVIRONMENT, EnvironmentBroker} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {InputTextModule} from 'primeng/inputtext';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {Address} from '../../../entities/proposal-page-object/address';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';

interface AddressSectionForm {
  address: FormControl<string | null>;
  unitNumber: FormControl<string | null>;
  city: FormControl<string | null>;
  postalCode: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-address-section',
  templateUrl: './address-section.component.html',
  imports: [CommonModule, ReactiveFormsModule, CardModule, ButtonModule, InputTextModule, NgxMaskDirective],
})
export class AddressSectionComponent extends BaseSectionComponent<FormGroup<AddressSectionForm>, Address> {
  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  simulateAddress(): void {
    this.form.setValue({
      address: '1234 Street',
      unitNumber: '1',
      city: 'Ottawa',
      postalCode: 'H0H0H0',
    });
  }

  private createForm(): FormGroup<AddressSectionForm> {
    const form = this.fb.group<AddressSectionForm>({
      address: this.fb.control(null, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(75),
        validators.noSpecialCharacter,
      ]),
      unitNumber: this.fb.control(null, Validators.maxLength(5)),
      city: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(25),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      postalCode: this.fb.control(null, [Validators.required, validators.postalCode]),
    });

    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((_) => {
        this.propsChange.emit({output: this.formToOutput()});
      });

    return form;
  }
}
